import { dbCols } from '@cc/schema';
import { Create } from '@refinedev/antd';
import {
  useCustom,
  useCustomMutation,
  useGetLocale,
  useWarnAboutChange,
} from '@refinedev/core';
import { useActor } from '@xstate/react';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { Form, message, Select, Tabs } from 'antd';
import enUSPicker from 'antd/lib/date-picker/locale/en_US';
import ruPicker from 'antd/lib/date-picker/locale/ru_RU';
import { useWatch } from 'antd/lib/form/Form';
import {
  endOfDay,
  formatDuration,
  intervalToDuration,
  parseISO,
  roundToNearestMinutes,
} from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import qs from 'qs';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { TherapistActivityReactContext } from '../../App';
import { DateFnsDatePicker } from '../../components/Pickers';
import { SelectClient } from '../../components/SelectClient';
import { useClientOptions } from '../../hooks/client-options';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { Session } from '../../schema/be-graphql-generated';
import { ProgressNote } from './create/ProgressNote';
import { HomeworkSinceLastSessionPanel } from '../homework/HomeworkSinceLastSessionPanel';
import { CheckInSessionPanel } from '../checkIn/CheckInSessionPanel';
import useStyles from './styles.style';

const { RangePicker } = DateFnsDatePicker;

export const SessionsCreate = () => {
  const { t, i18n } = useTranslation();
  // F-b19 (founder 2026-09-21): the session form counts as activity on the filtered client too
  useActivityTracking(`${dbCols.sessionsRelations}/create`);
  const location = useLocation();

  const { styles } = useStyles();

  const getLocale = useGetLocale();

  const currentLocale = useMemo(() => getLocale(), [i18n.languages[0]]);

  const { briefClientInfo, clientsLoading } = useClientOptions();

  const therapistActivityService = useContext(TherapistActivityReactContext);
  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );

  const { clientId: locationClientId, sessionsId: sessionIdFromLocation } =
    useMemo(
      () =>
        qs.parse(
          location.search.charAt(0) === '?'
            ? location.search.slice(1)
            : location.search,
        ),
      [location.search],
    ) || {};

  const [clientId, setClientId] = useState<string | undefined>(
    // E57 (Q-e57g1-2 A rule 4): a create page opened with ?clientId= (Create on a client's tool tab, Use a tool) keeps
    // THAT client; the header bar's filter is the fallback
    (locationClientId as string) ||
      therapistActivityState.context.filterClientId,
  );

  const initialValues = useMemo(() => {
    return {
      placeOfServiceCode: '10',
      sessionRange: [] as [Date, Date] | [],
    };
  }, []);

  const [form] = Form.useForm<typeof initialValues>();

  const sessionRange = useWatch('sessionRange', form);
  const placeOfServiceCode = useWatch('placeOfServiceCode', form);

  const [sessionId, setSessionId] = useState(
    sessionIdFromLocation as string | undefined,
  );

  const {
    data: sessionData,
    isLoading: sessionLoading,
    refetch: refetchSession,
    error: sessionError,
  } = useCustom<Session>({
    url: '',
    method: 'get',
    meta: {
      operation: 'session',
      fields: [
        '_id',
        '_to',
        'createdAt',
        'updatedAt',
        'finishedAt',
        'placeOfServiceCode',
      ],

      variables: {
        id: {
          name: 'id',
          type: 'ID',
          value: sessionId,
        },
      },
    },
  });

  useEffect(() => {
    if (sessionData?.data) {
      const {
        __typename,
        _id,
        _key,
        _rev,
        createdAt,
        updatedAt,
        finishedAt,
        ...rest
      } = sessionData?.data;

      const sessionRange =
        createdAt && finishedAt
          ? [parseISO(createdAt), parseISO(finishedAt)]
          : createdAt
          ? [parseISO(createdAt)]
          : [];

      form?.setFieldsValue({
        placeOfServiceCode: rest.placeOfServiceCode || '10',
        sessionRange,
      });

      setClientId(sessionData?.data._to);
    }
  }, [sessionData]);

  const {
    mutate: upsertSession,
    data: upsertSessionData,
    isLoading: upsertSessionLoading,
    error: upsertSessionError,
  } = useCustomMutation<{ session: Session }>();

  const saveData = useCallback(
    async (values?: typeof initialValues) => {
      const { sessionRange, placeOfServiceCode } =
        values || form?.getFieldsValue();
      const isReady = true;

      if (!clientId || !isReady) {
        return;
      }

      const res = await upsertSession({
        url: '',
        method: 'post',
        values: {},
        meta: {
          operation: 'upsertSession',
          fields: ['_id'],
          variables: {
            input: {
              name: 'input',
              type: 'SessionInput',
              value: {
                data: {
                  placeOfServiceCode,
                  createdAt: sessionRange[0],
                  finishedAt: sessionRange[1],
                },
                where: {
                  _id: sessionId,
                  _to: clientId,
                },
              },
            },
          },
        },
      });
      message.success(t('pages.sessions.saveSuccess'));
    },
    [clientId, sessionId, t],
  );

  /**
   * EXPLICIT SAVE (F-b1, founder 2026-09-21: "Explicit save" — the same ruling E16's form and E18's
   * notes tab already follow). The form used to autosave every control change on a 1 s debounce
   * with the Save button HIDDEN, so a range dragged twice and a place code picked three times were
   * five `SessionUpdated`s about a button, not a decision (T99 (f)). A change now only marks the
   * form dirty (and arms the leave-warning); Save writes the whole form once.
   */
  const { setWarnWhen } = useWarnAboutChange();
  const [dirty, setDirty] = useState(false);
  const onValuesChange = useCallback(() => {
    setDirty(true);
    setWarnWhen(true);
  }, [setWarnWhen]);
  const onSave = useCallback(async () => {
    try {
      await saveData();
    } catch {
      return; // still unsaved: keep the guard up and leave Save offered
    }
    setDirty(false);
    setWarnWhen(false);
  }, [saveData, setWarnWhen]);

  useEffect(() => {
    // @ts-ignore
    if (upsertSessionData?.data?._id) {
      // @ts-ignore

      if (!sessionId) {
        // @ts-ignore
        setSessionId(upsertSessionData?.data?._id);
      }
    }
  }, [upsertSessionData]);

  const {
    diagnosesOnClient,
    preferredPronounsOnclient,
    accessLevel,
    clientName,
  } = useMemo(() => {
    const clientInfo = briefClientInfo.find((c) => c._id === clientId);

    return {
      diagnosesOnClient: (clientInfo?.diagnoses || []).filter(
        (d) => !!d,
      ) as string[],
      preferredPronounsOnclient: clientInfo?.preferredPronouns,
      accessLevel: clientInfo?.accessLevel,
      clientName:
        [clientInfo?.firstName, clientInfo?.lastName]
          .filter(Boolean)
          .join(' ') ||
        clientInfo?.email ||
        '',
    };
  }, [briefClientInfo, clientId]);

  const generalInfomation = (
    <Form<typeof initialValues>
      form={form}
      initialValues={{
        ...initialValues,
      }}
      onValuesChange={onValuesChange}
      onFinish={onSave}
      labelCol={{ span: 4 }}
      wrapperCol={{ span: 24 }}
      labelWrap
    >
      <div className={styles['sessions--mental-status']}>
        <Form.Item
          name="sessionRange"
          label={t('pages.sessions.sessionRange')}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          className={styles['sessions--data-column--xxl']}
        >
          <RangePicker
            locale={currentLocale?.startsWith('ru') ? ruPicker : enUSPicker}
            showTime={{
              format: currentLocale?.startsWith('ru') ? 'HH:mm' : 'hh:mm a',
            }}
            format={
              currentLocale?.startsWith('ru')
                ? 'yyyy-MM-dd HH:mm'
                : 'yyyy-MM-dd hh:mm a'
            }
            disabledDate={(current) => {
              return current && current > endOfDay(new Date());
            }}
            minuteStep={5}
            onChange={(dates) => {
              const roundedStart = dates?.[0]
                ? roundToNearestMinutes(dates?.[0], {
                    nearestTo: 1,
                    roundingMethod: 'floor',
                  })
                : undefined;
              const roundedEnd = dates?.[1]
                ? roundToNearestMinutes(dates?.[1], {
                    nearestTo: 1,
                    roundingMethod: 'ceil',
                  })
                : undefined;
              form?.setFieldsValue({
                // @ts-ignore
                sessionRange: [roundedStart, roundedEnd],
              });
            }}
          />
        </Form.Item>

        <Form.Item
          name="sessionRange"
          label={t('pages.sessions.duration')}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          className={styles['sessions--data-column--s']}
        >
          <div className={styles['duration--content']}>
            {sessionRange?.[0] && sessionRange?.[1]
              ? formatDuration(
                  intervalToDuration({
                    start: sessionRange[0],
                    end: sessionRange[1],
                  }),
                  {
                    format: ['days', 'hours', 'minutes'],

                    locale: currentLocale?.startsWith('ru') ? ru : enUS,
                  },
                )
              : t('common.na')}
          </div>
        </Form.Item>

        <Form.Item
          name="placeOfServiceCode"
          label={t('pages.sessions.placeOfServiceLabel')}
          tooltip={t('pages.sessions.placeOfServiceLabelTip')}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          className={styles['sessions--data-column--xxxxl']}
        >
          <Select
            options={(
              t('pages.sessions.placeOfServiceCodes', {
                returnObjects: true,
              }) as any[]
            ).map((format, idx) => ({
              label: format.code + ' - ' + format.name,
              value: format.code,
            }))}
          />
        </Form.Item>
      </div>
    </Form>
  );

  return (
    <Create
      title={t('pages.sessions.title')}
      isLoading={clientsLoading}
      saveButtonProps={{
        'onClick': onSave,
        'loading': upsertSessionLoading,
        // nothing to save is not a thing to offer: the button follows the dirty flag that also
        // drives the leave-warning
        'disabled': !clientId || sessionLoading || !dirty,
        'data-testid': 'cc-session-form--save',
      }}
    >
      <div className={styles['session-container']}>
        <SelectClient clientId={clientId} setClientId={setClientId} />
      </div>

      <Tabs
        data-testid="cc-session-form-tabs"
        items={[
          ...(t('pages.sessions.tabs', { returnObjects: true }) as any[]).map(
            (i, idx) => ({
              label: (
                <span>
                  <b>{i}</b>
                </span>
              ),

              size: 'large',
              key: String(idx),
              children:
                idx === 0 ? (
                  generalInfomation
                ) : idx === 1 ? (
                  <ProgressNote
                    sessionId={sessionId || ''}
                    diagnosesOnClient={diagnosesOnClient}
                    preferredPronounsOnclient={preferredPronounsOnclient}
                    clientId={clientId}
                    placeOfServiceCode={placeOfServiceCode}
                    sessionRange={sessionRange}
                    accessLevel={accessLevel}
                  />
                ) : null, // tabs 2 and 3 are disabled: nothing to show

              disabled: [2, 3].includes(idx),
            }),
          ),
          /**
           * E34 · the session form's "Homework since last session" panel (cc-homework,
           * HomeworkSinceLastSession — E17's Review Homework): what the client did since the
           * previous session, and "Plan for next session" (the same Assign drawer as the hub).
           */
          {
            key: 'homework',
            label: (
              <span data-testid="cc-session-form-tab-homework">
                <b>{t('homework.session.tab')}</b>
              </span>
            ),
            children: (
              <HomeworkSinceLastSessionPanel
                clientId={clientId}
                clientName={clientName}
                sessionId={sessionId}
                locale={currentLocale?.startsWith('ru') ? 'ru' : 'en'}
              />
            ),
          },
          /**
           * E37 · the session form's "Check-in recap" (CheckInsSinceLastSession, beside E34's homework panel):
           * check-ins since the previous session — count, mood average, the lowest day with its note, sleep, the
           * drivers that came up most.
           */
          {
            key: 'checkin-recap',
            label: (
              <span data-testid="cc-session-form-tab-checkin-recap">
                <b>{t('checkIn.session.recapTab')}</b>
              </span>
            ),
            children: (
              <CheckInSessionPanel
                clientId={clientId}
                sessionId={sessionId}
                locale={currentLocale?.startsWith('ru') ? 'ru' : 'en'}
              />
            ),
          },
        ]}
      />
    </Create>
  );
};
